import React, { useState, useEffect } from 'react';
import { api } from '../context/AppContext';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';

/**
 * Performance Globale sparkline — shows the user's monthly trajectory
 * across the last N months. Placed at the top of the Mission Report.
 */
const PerformanceTimeline = ({ language = 'fr' }) => {
  const fr = language === 'fr';
  const t = (frTxt, enTxt) => (fr ? frTxt : enTxt);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/reports/mission/history')
      .then(res => { if (!cancelled) setReports(res.data?.reports || []); })
      .catch(() => { if (!cancelled) setReports([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  const data = (reports || [])
    .filter(r => typeof r.performance_globale === 'number')
    .map(r => ({
      label: `${r.month_label?.slice(0, 3) || ''} ${String(r.year).slice(-2)}`,
      score: r.performance_globale,
      raw_label: `${r.month_label || ''} ${r.year}`,
    }));

  // Need ≥2 points to draw a meaningful trajectory
  if (data.length < 2) {
    return (
      <div
        className="rounded-2xl border border-border/50 bg-card/40 p-4 mb-5 text-center"
        data-testid="performance-timeline-empty"
      >
        <Activity className="w-5 h-5 mx-auto text-muted-foreground mb-1.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          {t(
            'Ta trajectoire de performance apparaitra ici quand tu auras genere au moins 2 rapports mensuels.',
            'Your performance trajectory will appear here once you have at least 2 monthly reports.',
          )}
        </p>
      </div>
    );
  }

  const first = data[0].score;
  const last = data[data.length - 1].score;
  const delta = +(last - first).toFixed(1);
  let trendTheme;
  let TrendIcon;
  let trendLabel;

  if (delta >= 0.5) {
    trendTheme = { text: 'text-emerald-300', bg: 'from-emerald-500/20 to-emerald-500/5', stroke: '#10B981', dot: '#34D399' };
    TrendIcon = TrendingUp;
    trendLabel = t(`+${delta} pts vs depart`, `+${delta} pts vs start`);
  } else if (delta <= -0.5) {
    trendTheme = { text: 'text-amber-300', bg: 'from-amber-500/20 to-amber-500/5', stroke: '#F59E0B', dot: '#FBBF24' };
    TrendIcon = TrendingDown;
    trendLabel = t(`${delta} pts vs depart`, `${delta} pts vs start`);
  } else {
    trendTheme = { text: 'text-sky-300', bg: 'from-sky-500/20 to-sky-500/5', stroke: '#0EA5E9', dot: '#38BDF8' };
    TrendIcon = Minus;
    trendLabel = t('Stable', 'Stable');
  }

  return (
    <div
      className={`rounded-2xl border border-border/50 bg-gradient-to-br ${trendTheme.bg} p-4 mb-5`}
      data-testid="performance-timeline"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-heading uppercase tracking-wider text-foreground">
            {t('Trajectoire de Performance', 'Performance Trajectory')}
          </h3>
        </div>
        <div className={`flex items-center gap-1 ${trendTheme.text}`}>
          <TrendIcon className="w-3.5 h-3.5" />
          <span className="text-[11px] font-bold" data-testid="trajectory-delta">{trendLabel}</span>
        </div>
      </div>

      {/* Sparkline */}
      <div className="w-full" style={{ height: 80 }} data-testid="trajectory-sparkline">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id="trajLineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={trendTheme.stroke} stopOpacity={0.3} />
                <stop offset="100%" stopColor={trendTheme.stroke} stopOpacity={1} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="label"
              tick={{ fill: '#94A3B8', fontSize: 9 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-card border border-border rounded-lg px-2 py-1.5 shadow-lg">
                    <p className="text-[10px] uppercase text-muted-foreground">{d.raw_label}</p>
                    <p className="text-sm font-black text-foreground">{d.score}/10</p>
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="url(#trajLineGradient)"
              strokeWidth={2.5}
              dot={{ r: 3, fill: trendTheme.dot, stroke: '#fff', strokeWidth: 1 }}
              activeDot={{ r: 5, fill: trendTheme.dot, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive
              animationDuration={800}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Footer caption */}
      <p className="text-[10px] text-muted-foreground/70 mt-2 leading-snug">
        {t(
          `${data.length} mois suivis · Du depart "${data[0].raw_label}" a aujourd'hui "${data[data.length - 1].raw_label}".`,
          `${data.length} months tracked · From "${data[0].raw_label}" to today "${data[data.length - 1].raw_label}".`,
        )}
      </p>
    </div>
  );
};

export default PerformanceTimeline;
